import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import {
  StatsRow, StatCard, Section, Card, CardTitle, Alert,
} from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { RefreshCw, Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const fmtEur = (cents) =>
  `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEurCompact = (cents) => {
  const eur = Number(cents || 0) / 100;
  if (Math.abs(eur) >= 1000000) return `€ ${(eur / 1000000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}M`;
  if (Math.abs(eur) >= 1000) return `€ ${(eur / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}k`;
  return `€ ${eur.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
};
const fmtEurFromEur = (eur) =>
  `€ ${Number(eur || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PLAN_LABEL = {
  internal: "Internal",
  professional: "Professional",
  business: "Business",
  enterprise: "Enterprise",
  unknown: "Sconosciuto",
};
const PIE_COLORS = ["#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#94A3B8"];

const monthLabel = (year, month) => {
  const names = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  return `${names[(month - 1) % 12]} ${String(year).slice(2)}`;
};

export default function SuperAdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/revenue");
      setData(data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runMonthlyReset = async () => {
    if (!window.confirm("Eseguire la chiusura del mese corrente? Verrà creato uno snapshot e azzerati i contatori.")) return;
    setResetting(true);
    try {
      await api.post("/superadmin/run-monthly-reset");
      toast.success("Snapshot mensile eseguito");
      load();
    } catch (e) { toast.error("Errore"); }
    finally { setResetting(false); }
  };

  const exportTopOrgsCsv = () => {
    if (!data?.top_orgs?.length) return;
    const rows = [
      ["Org ID", "Nome", "Ricavi mese (€)", "Costo OpenAPI (€)", "Margine (€)"],
      ...data.top_orgs.map((o) => [
        o.org_id, o.name, o.revenue_eur.toFixed(2), o.cost_eur.toFixed(2),
        (o.revenue_eur - o.cost_eur).toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "top-organizzazioni.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const history = useMemo(() => {
    if (!data?.usage_history?.length) return [];
    // group by month/year (across orgs)
    const map = {};
    data.usage_history.forEach((h) => {
      const key = `${h.year}-${String(h.month).padStart(2, "0")}`;
      if (!map[key]) map[key] = { key, year: h.year, month: h.month, revenue: 0, cost: 0 };
      map[key].revenue += (h.estimated_revenue_cents || 0) / 100;
      map[key].cost += (h.total_cost_cents || 0) / 100;
    });
    return Object.values(map)
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(-12)
      .map((m) => ({ ...m, label: monthLabel(m.year, m.month), margin: m.revenue - m.cost }));
  }, [data]);

  if (loading) return <div className="text-sm text-text-muted">Caricamento…</div>;
  if (!data) return <LoadErrorBox what="i ricavi" onRetry={load} testId="sa-revenue-load-error" />;

  const arpu = data.top_orgs.length ? (data.revenue_month_cents / data.top_orgs.length) : 0;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-medium tracking-widest uppercase text-text-muted mb-2">Wattline · Super admin</div>
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Ricavi e margini</h1>
          <p className="text-base text-text-secondary mt-2">Performance economica della piattaforma</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="h-10 rounded-md border-border" data-testid="sa-revenue-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Aggiorna
          </Button>
          <Button variant="outline" onClick={runMonthlyReset} disabled={resetting}
                  className="h-10 rounded-md border-border" data-testid="sa-revenue-monthly-reset">
            Chiudi mese {resetting && "…"}
          </Button>
        </div>
      </div>

      <StatsRow columns={4}>
        <StatCard label="MRR" value={fmtEurCompact(data.mrr_cents)}
                  detail="Ricorrente mensile" testId="sa-rev-mrr" />
        <StatCard label="Ricavi mese" value={fmtEurCompact(data.revenue_month_cents)}
                  detail="MRR + extra ricerche" testId="sa-rev-month" />
        <StatCard label="Margine mese" value={fmtEurCompact(data.margin_month_cents)}
                  detail={`${data.margin_pct}% margine`}
                  detailColor={data.margin_month_cents >= 0 ? "success" : "danger"}
                  testId="sa-rev-margin" />
        <StatCard label="ARPU" value={fmtEurCompact(arpu)}
                  detail={`${data.top_orgs.length} org. attive`} testId="sa-rev-arpu" />
      </StatsRow>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" testId="sa-rev-history-card">
          <CardTitle>Andamento ricavi/costi (ultimi 12 mesi)</CardTitle>
          {history.length === 0 ? (
            <div className="text-sm text-text-muted py-12 text-center">
              Nessuno snapshot storico disponibile.<br/>
              Esegui &quot;Chiudi mese&quot; al rinnovo del mese per generare lo storico.
            </div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={history} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => `€${Math.round(v).toLocaleString("it-IT")}`}
                         tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} width={70} />
                  <Tooltip formatter={(v) => fmtEurFromEur(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" name="Ricavi" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cost" name="Costo OpenAPI" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="margin" name="Margine" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card testId="sa-rev-byplan-card">
          <CardTitle>Ricavi per piano (mese)</CardTitle>
          {data.revenue_by_plan.length === 0 ? (
            <div className="text-sm text-text-muted py-8 text-center">Nessun dato</div>
          ) : (
            <>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.revenue_by_plan} dataKey="revenue_eur" nameKey="plan_id"
                         innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {data.revenue_by_plan.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtEurFromEur(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 text-sm">
                {data.revenue_by_plan.map((p, i) => (
                  <div key={p.plan_id} className="flex items-center justify-between" data-testid={`sa-rev-plan-${p.plan_id}`}>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-text-secondary">{PLAN_LABEL[p.plan_id] || p.plan_id}</span>
                    </span>
                    <span className="tabular-nums font-medium text-text-primary">{fmtEurFromEur(p.revenue_eur)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      <Card testId="sa-rev-toporgs-card">
        <div className="flex items-center justify-between mb-4">
          <CardTitle>Top organizzazioni per ricavo (mese)</CardTitle>
          <Button variant="outline" size="sm" onClick={exportTopOrgsCsv} className="rounded-md border-border h-8" data-testid="sa-rev-export-csv">
            <Download size={13} className="mr-1.5" /> Esporta CSV
          </Button>
        </div>
        {data.top_orgs.length === 0 ? (
          <div className="text-sm text-text-muted py-8 text-center">Nessuna organizzazione</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-rev-toporgs-table">
              <thead className="bg-subtle text-text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Organizzazione</th>
                  <th className="text-right px-4 py-2.5 font-medium">Ricavi mese</th>
                  <th className="text-right px-4 py-2.5 font-medium">Costo OpenAPI</th>
                  <th className="text-right px-4 py-2.5 font-medium">Margine</th>
                  <th className="text-right px-4 py-2.5 font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {data.top_orgs.map((o) => {
                  const margin = o.revenue_eur - o.cost_eur;
                  const pct = o.revenue_eur > 0 ? Math.round((margin / o.revenue_eur) * 100) : 0;
                  return (
                    <tr key={o.org_id} className="border-t border-border hover:bg-subtle" data-testid={`sa-rev-org-${o.org_id}`}>
                      <td className="px-4 py-2.5">
                        <Link to={`/superadmin/organizations/${o.org_id}`} className="text-accent hover:underline font-medium">
                          {o.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-text-primary">{fmtEurFromEur(o.revenue_eur)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-text-secondary">{fmtEurFromEur(o.cost_eur)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        <span className={margin >= 0 ? "text-success-strong" : "text-danger-strong"}>{fmtEurFromEur(margin)}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-xs text-text-muted">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Section>
        <Alert variant="info">
          I ricavi mensili includono <strong>MRR</strong> (canone fisso per organizzazione attiva) più eventuali <strong>ricerche extra</strong>
          oltre la quota inclusa nel piano. Il margine è il delta vs. il costo reale OpenAPI.
        </Alert>
      </Section>
    </div>
  );
}
